import { LEADERSHIP_PHOTO_MAX_BYTES } from './website-leadership';

function encode(bitmap: ImageBitmap, maxEdge: number, type: string): Promise<Blob | null> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare this photo. Please try another image.');
  if (type === 'image/jpeg') { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.92));
}

/** Preserve sharp portraits: one high-quality encode, with a bounded dimension retry. */
export async function prepareLeadershipPhotoFile(file: File): Promise<File> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Choose a PNG, JPEG or WebP photo.');
  if (!file.size) throw new Error('Choose a non-empty photo.');
  if (file.size > 20 * 1024 * 1024) throw new Error('Choose a photo smaller than 20 MiB.');
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch { throw new Error('Could not read this photo. Please try another PNG, JPEG or WebP image.'); }
  try {
    if (!bitmap.width || !bitmap.height) throw new Error('Could not read this photo.');
    if (Math.max(bitmap.width, bitmap.height) <= 1600 && file.size <= 1024 * 1024) return file;
    for (const edge of [1600, 1200]) {
      let blob = await encode(bitmap, edge, 'image/webp');
      if (!blob || blob.type !== 'image/webp') blob = await encode(bitmap, edge, 'image/jpeg');
      if (!blob || !['image/webp', 'image/jpeg'].includes(blob.type) || !blob.size) throw new Error('Could not prepare this photo. Please try another image.');
      if (blob.size <= LEADERSHIP_PHOTO_MAX_BYTES) return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`, { type: blob.type });
    }
    throw new Error('This photo is still too large at high quality. Please choose a smaller photo (up to 1.5 MiB after preparation).');
  } catch (error) {
    if (error instanceof Error && /Could not|too large/.test(error.message)) throw error;
    throw new Error('Could not prepare this photo. Please try another image.');
  } finally { bitmap.close(); }
}
